// Import browser-compatible modules only
import { Api } from './api/commerceClient';
import type { AutocompleteOptions } from './modules/browser-autocomplete';

// Note: firebaseService and authService are not imported here as they contain Node.js code
// Firebase auth is handled directly in auth.js using the global firebase object

// Browser-compatible autocomplete module
let browserAutocompleteModule: typeof import('./modules/browser-autocomplete') | null = null;
let browserAutocompleteLoad: Promise<typeof import('./modules/browser-autocomplete')> | null = null;
async function loadBrowserAutocomplete() {
  if (!browserAutocompleteLoad) {
    browserAutocompleteLoad = import('./modules/browser-autocomplete').then(module => {
      browserAutocompleteModule = module;
      return module;
    }).catch(error => { browserAutocompleteLoad = null; throw error; });
  }
  return browserAutocompleteLoad;
}
const pendingAutocomplete = new Map<HTMLInputElement, AbortController>();

function resolveAutocompleteInput(target: string | HTMLInputElement | { current?: HTMLInputElement | null } | null | undefined): HTMLInputElement {
  if (typeof document === 'undefined') {
    throw new Error('Autocomplete requires a browser environment');
  }
  if (!target) {
    throw new Error('Autocomplete input target is required');
  }

  let el: any = target;
  if (typeof target === 'string') {
    el = document.getElementById(target);
  } else if ((target as { current?: HTMLInputElement | null })?.current) {
    el = (target as { current?: HTMLInputElement | null }).current;
  }

  if (!el) {
    throw new Error('Autocomplete input element not found');
  }

  if (!(el instanceof HTMLInputElement)) {
    throw new Error('Autocomplete target must be an input element');
  }

  return el;
}

function resolveAutocompleteCallback(arg1: any, arg2: any) {
  if (typeof arg1 === 'function' && !arg2) return arg1;
  if (typeof arg2 === 'function') return arg2;
  if (typeof arg1 === 'function') return arg1;
  return () => {};
}

// Lazy-loaded modules for code splitting
let mapsModule: any = null;
let calcModule: any = null;

// Dynamic imports for heavy features
async function loadMaps() {
  if (!mapsModule) {
    mapsModule = await import('./maps');
  }
  return mapsModule;
}

async function loadCalc() {
  if (!calcModule) {
    calcModule = await import('./calc');
  }
  return calcModule;
}

// Firebase initialization is handled in firebase-config.js and auth.js
// No additional initialization needed here

// Expose minimal surface to window for existing HTML to call without refactor
(function expose(){
  const w = window as any;

  // Capture the caller's DOM owner before an asynchronous module load.
  const captureMapOwner = (owner?: HTMLElement) => owner ?? document.getElementById('map');
  const lazyMaps = {
    async ensureMap(owner?: HTMLElement) {
      const capturedOwner = captureMapOwner(owner);
      if (!capturedOwner) return null;
      const maps = await loadMaps();
      return maps.ensureMap(capturedOwner);
    },
    async clearMap(owner?: HTMLElement) {
      const capturedOwner = captureMapOwner(owner);
      if (!capturedOwner) return;
      const maps = await loadMaps();
      return maps.clearMap(capturedOwner);
    },
    async addMarker(lat: number, lng: number, label?: string, owner?: HTMLElement) {
      const capturedOwner = captureMapOwner(owner);
      if (!capturedOwner) return;
      const maps = await loadMaps();
      return maps.addMarker(lat, lng, label, capturedOwner);
    },
    async drawRoute(coords: any[], owner?: HTMLElement) {
      const capturedOwner = captureMapOwner(owner);
      if (!capturedOwner) return;
      const maps = await loadMaps();
      return maps.drawRoute(coords, capturedOwner);
    },
    disposeMap(owner: HTMLElement) {
      // React cleanup must finish now, before the same DOM owner can be reused.
      mapsModule?.disposeMap(owner);
    },
  };

  // Lazy-loaded calculator utilities
  const lazyCalc = {
    async computeEtaMinutes(distanceKm: number, avgSpeedKmh?: number, bufferMin?: number) {
      const calc = await loadCalc();
      return calc.computeEtaMinutes(distanceKm, avgSpeedKmh, bufferMin);
    },
    async normalizeRouteDistanceKm(resp: any) {
      const calc = await loadCalc();
      return calc.normalizeRouteDistanceKm(resp);
    }
  };


  // Browser-compatible proximity functions
  const lazyProximity = {
    // API-based functions using @erunna/api-client
    async placesSearch(query: string, options?: any) {
      // Use the API client for places search
      return Api.maps.search(query, options?.location);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async placesDetails(placeId: string, options?: any) {
      // Use the API client for place details
      return Api.maps.details(placeId);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async directions(origin: any, destination: any, _options?: any) {
      // Use the API client for directions (options param kept for backwards compat but unused)
      return Api.maps.directions(origin, destination);
    },
    async pricingTransport(...args: any[]) {
      const params = (args.length === 1 && typeof args[0] === 'object' && args[0] !== null)
        ? args[0]
        : { transportMode: args[0], distanceKm: args[1] };

      // Handle legacy format with just transportMode and distanceKm
      if (params.transportMode !== undefined && params.distanceKm !== undefined) {
        return Api.pricing.transport(params.transportMode, params.distanceKm);
      }
      // Handle new format with origin/destination - calculate distance first
      if (params.origin && params.destination) {
        try {
          // Get directions to calculate distance
          const directions = await Api.maps.directions(params.origin, params.destination);
          const distanceKm = await lazyCalc.normalizeRouteDistanceKm(directions);
          return Api.pricing.transport(params.vehicleType || 'car', distanceKm);
        } catch (error) {
          console.error('Failed to calculate distance for pricing:', error);
          throw error;
        }
      }
      throw new Error('Either provide distanceKm or both origin and destination');
    },
    async pricingParcel(...args: any[]) {
      const params = (args.length === 1 && typeof args[0] === 'object' && args[0] !== null)
        ? args[0]
        : { distanceKm: args[0], parcelSize: args[1], pickup: args[2], dropoff: args[3] };

      // For parcel pricing, use the estimate endpoint with 'parcel' service type
      if (params.pickup || params.origin) {
        const pickup = params.pickup || params.origin;
        const dropoff = params.dropoff || params.destination;
        return Api.pricing.estimate(pickup, dropoff, 'parcel', null, params.distanceKm);
      }
      throw new Error('Pickup/origin and dropoff/destination required for pricing');
    },
    async pricingEstimate(...args: any[]) {
      const params = (args.length === 1 && typeof args[0] === 'object' && args[0] !== null)
        ? args[0]
        : {
            pickup: args[0],
            dropoff: args[1],
            serviceType: args[2],
            transportMode: args[3],
            distanceKm: args[4]
          };

      // Use the API client for pricing estimate
      const pickup = params.pickup || params.origin;
      const dropoff = params.dropoff || params.destination;
      const serviceType = params.serviceType || 'transport';
      
      if (pickup && dropoff) {
        return Api.pricing.estimate(pickup, dropoff, serviceType, params.transportMode, params.distanceKm);
      }
      throw new Error('Pickup and dropoff locations required for pricing estimate');
    },
    // Response normalizers using calc module
    async normalizeRouteResponse(response: any) {
      const calc = await loadCalc();
      return calc.normalizeRouteResponse(response);
    },
    async normalizeParcelPricingResponse(response: any) {
      const calc = await loadCalc();
      return calc.normalizeParcelPricingResponse(response);
    },
    async normalizeAuctionPricingResponse(response: any) {
      const calc = await loadCalc();
      return calc.normalizeAuctionPricingResponse(response);
    },
    // Caller cancellation releases its waiter; the module and SDK remain shared.
    async resolveTextToPlace(text: string, options?: AutocompleteOptions) {
      const autocompleteModule = await loadBrowserAutocomplete();
      if (options?.signal?.aborted) throw new DOMException('Autocomplete cancelled', 'AbortError');
      return autocompleteModule.browserAutocomplete.resolveTextToPlace(text, options);
    },
    async attachAutocomplete(
      input: string | HTMLInputElement | { current?: HTMLInputElement | null },
      maybeMenuOrCallback?: string | ((payload: unknown) => void),
      maybeCallbackOrOptions?: ((payload: unknown) => void) | AutocompleteOptions,
      options?: AutocompleteOptions,
    ) {
      const inputEl = resolveAutocompleteInput(input);
      const callback = resolveAutocompleteCallback(maybeMenuOrCallback, maybeCallbackOrOptions);
      const attachOptions = typeof maybeCallbackOrOptions === 'object' ? maybeCallbackOrOptions : options;
      pendingAutocomplete.get(inputEl)?.abort();
      const controller = new AbortController();
      const cancel = () => controller.abort();
      attachOptions?.signal?.addEventListener('abort', cancel, { once: true });
      if (attachOptions?.signal?.aborted) controller.abort();
      pendingAutocomplete.set(inputEl, controller);
      try {
        const autocompleteModule = await loadBrowserAutocomplete();
        if (controller.signal.aborted) throw new DOMException('Autocomplete cancelled', 'AbortError');
        if (typeof maybeMenuOrCallback === 'string') inputEl.setAttribute('aria-controls', maybeMenuOrCallback);
        return await autocompleteModule.browserAutocomplete.attach(inputEl, callback, { ...attachOptions, signal: controller.signal });
      } finally {
        attachOptions?.signal?.removeEventListener('abort', cancel);
        if (pendingAutocomplete.get(inputEl) === controller) pendingAutocomplete.delete(inputEl);
      }
    },
    detachAutocomplete(input: string | HTMLInputElement | { current?: HTMLInputElement | null }) {
      const inputEl = resolveAutocompleteInput(input);
      pendingAutocomplete.get(inputEl)?.abort();
      pendingAutocomplete.delete(inputEl);
      browserAutocompleteModule?.browserAutocomplete.detach(inputEl);
      delete inputEl.dataset.autocompleteAttached;
    }

  };

  w.Bridge = {
    Api,
    // auth is handled in auth.js using global firebase
    maps: lazyMaps,
    calc: lazyCalc,
    // Proximity functions with lazy loading
    ...lazyProximity,
  };
})();

